import {Flashlight, Loader2, RefreshCcw, X} from "lucide-react";

import {useEffect, useLayoutEffect, useRef, useState} from "react";

import {toast} from "sonner";

import {requestCameraVideoStream} from "@/src/modules/core/lib/camera-permission";
import {releaseMediaStream} from "@/src/modules/core/lib/camera-environment";
import {
  findTorchCapableDeviceId,
  getCameraFlashSupportWithRetry,
  getFillLightModeForCapture,
  isAndroidRearCameraFlashOptimistic,
  requestCameraStreamForDevice,
  resolveCameraFlashSupport,
  setCameraTorch,
  shouldShowCameraFlashControl,
} from "@/src/modules/core/lib/camera-flash";
import {captureInAppPhoto} from "@/src/modules/core/lib/capture-in-app-photo";
import {
  captureCameraFlashEnableFailedTelemetry,
  captureCameraFlashProbeTelemetry,
  captureCameraFlashToggleTelemetry,
} from "@/src/modules/core/lib/glitchtip/camera-telemetry";
import {resolveFeatureFromWindowLocation} from "@/src/modules/core/lib/glitchtip/resolve-feature";



/**

 * In-page getUserMedia preview — WebView stays foreground (no Android PWA OOM kill).

 * Parent acquires the MediaStream on pointerdown and only opens this UI once the stream is ready.

 */

export default function CameraCapture({

  open,

  stream,

  onCapture,

  onCaptureFailed,

  onClose,

  onStreamChange,

  onStreamError,

  captureFeature,

  captureOperation,

}) {

  const videoRef = useRef(null);

  const streamRef = useRef(null);

  const pendingStreamRef = useRef(null);

  const onCloseRef = useRef(onClose);

  const onStreamErrorRef = useRef(onStreamError);

  const onCaptureFailedRef = useRef(onCaptureFailed);

  const onStreamChangeRef = useRef(onStreamChange);

  onCloseRef.current = onClose;

  onStreamErrorRef.current = onStreamError;

  onCaptureFailedRef.current = onCaptureFailed;

  onStreamChangeRef.current = onStreamChange;



  const [isSwitching, setIsSwitching] = useState(false);

  const [isCapturing, setIsCapturing] = useState(false);

  const [videoElement, setVideoElement] = useState(null);

  const [flashSupport, setFlashSupport] = useState(null);

  const [flashEnabled, setFlashEnabled] = useState(false);

  const [facingMode, setFacingMode] = useState("environment");

  const flashSupportRef = useRef(null);

  flashSupportRef.current = flashSupport;

  const getTelemetryInput = () => ({
    feature: captureFeature ?? resolveFeatureFromWindowLocation(),
    operation: captureOperation,
  });



  const detachVideo = () => {

    if (videoRef.current) {

      videoRef.current.srcObject = null;

    }

  };



  const releaseActiveStream = () => {

    const mediaStream = streamRef.current ?? stream;

    detachVideo();

    releaseMediaStream(mediaStream, videoRef.current);

    streamRef.current = null;

    pendingStreamRef.current = null;

  };



  const bindStreamToVideo = (mediaStream) => {

    streamRef.current = mediaStream;

    pendingStreamRef.current = null;

    const video = videoRef.current;

    if (video) {

      video.srcObject = mediaStream;

      video.play?.().catch(() => {});

    } else {

      pendingStreamRef.current = mediaStream;

    }

  };



  useLayoutEffect(() => {

    if (!videoElement || !pendingStreamRef.current) {

      return;

    }



    videoElement.srcObject = pendingStreamRef.current;

    videoElement.play?.().catch(() => {});

    pendingStreamRef.current = null;

  }, [videoElement]);



  useEffect(() => {

    if (!open || !stream) {

      return;

    }



    bindStreamToVideo(stream);

    return detachVideo;

  }, [open, stream]);



  useEffect(() => {

    if (!open || !stream) {

      setFlashSupport(null);

      setFlashEnabled(false);

      return;

    }



    const track = stream.getVideoTracks()[0];

    if (!track || !videoElement) {

      return;

    }



    let cancelled = false;

    let warmupTimer;



    const probeFlashSupport = () => {

      warmupTimer = window.setTimeout(() => {

        if (cancelled) {

          return;

        }

        void getCameraFlashSupportWithRetry(track).then(({support, attempts}) => {

          if (cancelled) {

            return;

          }

          setFlashSupport(support);

          const optimisticUi = isAndroidRearCameraFlashOptimistic(facingMode);

          captureCameraFlashProbeTelemetry({
            ...getTelemetryInput(),
            facingMode,
            torchProbed: support.torch,
            flashOnCaptureProbed: support.flashOnCapture,
            showControl: shouldShowCameraFlashControl(support, facingMode),
            optimisticUi,
            probeAttempts: attempts,
          });

          if (!shouldShowCameraFlashControl(support, facingMode)) {

            setFlashEnabled(false);

          }

        });

      }, 250);

    };



    // Torch / fillLightMode capabilities appear only after preview is streaming (crbug.com/711524).

    if (

      !videoElement.paused &&

      videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA

    ) {

      probeFlashSupport();

    } else {

      videoElement.addEventListener("playing", probeFlashSupport, {once: true});

    }



    return () => {

      cancelled = true;

      window.clearTimeout(warmupTimer);

      videoElement.removeEventListener("playing", probeFlashSupport);

    };

  }, [open, stream, videoElement, facingMode]);



  useEffect(() => {

    if (open) {

      return;

    }

    releaseActiveStream();

    setFlashSupport(null);

    setFlashEnabled(false);

    setFacingMode("environment");

    facingModeRef.current = "environment";

  }, [open]);



  useEffect(() => {

    return () => {

      releaseActiveStream();

    };

  }, []);



  const reportStreamError = (error) => {

    const name = error?.name ?? "UnknownError";

    if (name === "NotAllowedError" || name === "SecurityError") {

      onStreamErrorRef.current?.("permission_denied", {mediaErrorName: name});

    } else {

      onStreamErrorRef.current?.("unavailable", {

        mediaErrorName: name,

        reason: "get_user_media_failed",

      });

    }

  };



  const handleCapture = async () => {

    const video = videoRef.current;

    const mediaStream = streamRef.current ?? stream;

    if (!video || !mediaStream || isCapturing) {

      return;

    }



    setIsCapturing(true);

    try {

      const probedSupport = flashSupportRef.current;
      const fillLightMode = getFillLightModeForCapture(flashEnabled, probedSupport);

      const result = await captureInAppPhoto(mediaStream, video, {

        fileName: `photo-${Date.now()}.jpg`,

        fillLightMode,

      });

      if (!result) {

        onCaptureFailedRef.current?.();

        toast.error("Couldn't capture the photo. Try again.");

        return;

      }

      // Release hardware before upload callbacks — keeps the OS camera indicator off.

      releaseActiveStream();

      const optimistic = isAndroidRearCameraFlashOptimistic(facingMode);

      onCapture?.(result.file, {

        method: result.method,

        sourceWidth: result.sourceWidth,

        sourceHeight: result.sourceHeight,

        flashEnabled,

        fillLightMode,

        torchProbed: flashSupportRef.current?.torch ?? false,

        flashOnCaptureProbed: flashSupportRef.current?.flashOnCapture ?? false,

        flashOptimisticUi: optimistic,

      });

      onCloseRef.current?.();

    } finally {

      setIsCapturing(false);

    }

  };



  const facingModeRef = useRef("environment");

  facingModeRef.current = facingMode;



  const resetFlashState = () => {

    setFlashEnabled(false);

    setFlashSupport(null);

  };



  const applyTorchToggle = async (track, nextEnabled, optimistic) => {
    let applied = await setCameraTorch(track, nextEnabled, {optimistic});

    if (!applied && nextEnabled && optimistic) {
      const currentDeviceId = track.getSettings?.().deviceId;
      // Samsung blocks opening a second lens while the preview track is active.
      releaseMediaStream(streamRef.current, videoRef.current);
      const torchDeviceId = await findTorchCapableDeviceId(currentDeviceId);
      if (torchDeviceId) {
        const nextStreamRequest = requestCameraStreamForDevice(torchDeviceId, facingMode);
        if (nextStreamRequest) {
          try {
            const nextStream = await nextStreamRequest;
            onStreamChangeRef.current?.(nextStream);
            bindStreamToVideo(nextStream);
            const nextTrack = nextStream.getVideoTracks()[0];
            const {support} = await getCameraFlashSupportWithRetry(nextTrack);
            setFlashSupport(support);
            applied = await setCameraTorch(nextTrack, true, {optimistic: false});
          } catch {
            applied = false;
          }
        }
      }

      if (!applied) {
        const restoreRequest = requestCameraVideoStream(facingMode);
        if (restoreRequest) {
          try {
            const restoredStream = await restoreRequest;
            onStreamChangeRef.current?.(restoredStream);
            bindStreamToVideo(restoredStream);
            const restoredTrack = restoredStream.getVideoTracks()[0];
            const {support} = await getCameraFlashSupportWithRetry(restoredTrack);
            setFlashSupport(support);
          } catch {
            onStreamErrorRef.current?.("unavailable", {reason: "torch_lens_switch_failed"});
          }
        }
      }
    }

    return applied;
  };

  const toggleFlash = () => {
    const mediaStream = streamRef.current ?? stream;
    const track = mediaStream?.getVideoTracks()[0];
    const probedSupport = flashSupportRef.current;
    const support = resolveCameraFlashSupport(probedSupport, facingMode);
    const optimistic = isAndroidRearCameraFlashOptimistic(facingMode);
    const probedCaptureFlash = probedSupport?.flashOnCapture ?? false;

    if (!track || !shouldShowCameraFlashControl(probedSupport, facingMode)) {
      return;
    }

    const nextEnabled = !flashEnabled;

    if (support.torch) {
      void applyTorchToggle(track, nextEnabled, optimistic).then((applied) => {
        const telemetry = {
          ...getTelemetryInput(),
          facingMode,
          enabled: nextEnabled,
          torchApplied: applied,
          optimisticUi: optimistic,
          hasCaptureFlash: probedCaptureFlash,
        };

        if (!applied && nextEnabled) {
          if (probedCaptureFlash) {
            captureCameraFlashToggleTelemetry({
              ...telemetry,
              torchApplied: false,
            });
            setFlashEnabled(true);
            toast.message("Flash will fire when you take the photo.");
            return;
          }

          captureCameraFlashEnableFailedTelemetry({
            ...getTelemetryInput(),
            facingMode,
            optimisticUi: optimistic,
          });
          toast.error("Couldn't turn on the flash on this device.");
          return;
        }

        captureCameraFlashToggleTelemetry(telemetry);
        setFlashEnabled(nextEnabled);
      });
      return;
    }

    if (!nextEnabled || probedCaptureFlash) {
      captureCameraFlashToggleTelemetry({
        ...getTelemetryInput(),
        facingMode,
        enabled: nextEnabled,
        torchApplied: false,
        optimisticUi: optimistic,
        hasCaptureFlash: probedCaptureFlash,
      });
      setFlashEnabled(nextEnabled);
      if (nextEnabled && probedCaptureFlash) {
        toast.message("Flash will fire when you take the photo.");
      }
      return;
    }

    captureCameraFlashEnableFailedTelemetry({
      ...getTelemetryInput(),
      facingMode,
      optimisticUi: optimistic,
    });
    toast.error("Couldn't turn on the flash on this device.");
  };



  const switchCamera = () => {
    if (isSwitching) {
      return;
    }

    const nextFacing = facingModeRef.current === "environment" ? "user" : "environment";
    const request = requestCameraVideoStream(nextFacing);
    if (!request) {
      onStreamErrorRef.current?.("unavailable", {reason: "get_user_media_unsupported"});
      return;
    }

    setIsSwitching(true);
    void request
      .then((nextStream) => {
        releaseMediaStream(streamRef.current);
        facingModeRef.current = nextFacing;
        setFacingMode(nextFacing);
        resetFlashState();
        onStreamChangeRef.current?.(nextStream);
        bindStreamToVideo(nextStream);
      })
      .catch(reportStreamError)
      .finally(() => setIsSwitching(false));
  };



  if (!open) {

    return null;

  }



  return (

    <div className="fixed inset-0 z-[70] flex flex-col bg-black">

      <div className="flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))]">

        <button

          type="button"

          onClick={() => {

            releaseActiveStream();

            onCloseRef.current?.();

          }}

          className="flex size-10 items-center justify-center rounded-full bg-white/10 text-white"

          aria-label="Close camera"

        >

          <X className="size-5" />

        </button>

        <div className="flex items-center gap-2">

          {shouldShowCameraFlashControl(flashSupport, facingMode) ? (

            <button

              type="button"

              onClick={toggleFlash}

              className={`flex size-10 items-center justify-center rounded-full text-white disabled:opacity-50 ${

                flashEnabled ? "bg-white text-black" : "bg-white/10"

              }`}

              aria-label={flashEnabled ? "Turn flash off" : "Turn flash on"}

              aria-pressed={flashEnabled}

              disabled={isSwitching || isCapturing}

            >

              <Flashlight className="size-5" />

            </button>

          ) : null}

          <button

          type="button"

          onClick={switchCamera}

          className="flex size-10 items-center justify-center rounded-full bg-white/10 text-white disabled:opacity-50"

          aria-label="Switch camera"

          disabled={isSwitching}

        >

          <RefreshCcw className="size-5" />

        </button>

        </div>

      </div>



      <div className="relative flex flex-1 items-center justify-center overflow-hidden">

        <video

          ref={(node) => {

            videoRef.current = node;

            setVideoElement(node);

          }}

          autoPlay

          playsInline

          muted

          className="h-full w-full object-contain"

        />

        {isSwitching ? (

          <div className="absolute inset-0 flex items-center justify-center text-white">

            <Loader2 className="size-8 animate-spin" />

          </div>

        ) : null}

      </div>



      <div className="flex items-center justify-center p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">

        <button

          type="button"

          onClick={handleCapture}

          disabled={isSwitching || isCapturing}

          className="flex size-16 items-center justify-center rounded-full border-4 border-white/80 bg-white/20 disabled:opacity-50"

          aria-label="Take photo"

        >

          {isCapturing ? (

            <Loader2 className="size-8 animate-spin text-white" />

          ) : (

            <span className="size-12 rounded-full bg-white" />

          )}

        </button>

      </div>

    </div>

  );

}

